<template>
  <main 
  v-if="page"
  id="content" 
  aria-labelledby="main-title"
  >
    
    <article>
      
      <ElementHeroBlog :content="page" />

      <Container class="px-0 pl-4 sm:pl-8 sm:px-0 md:px-8">
        <div class="md:grid md:grid-cols-12 lg:grid-cols-12 gap-8 relative">
          <ElementTableOfContents />
          <div class="md:col-start-4 md:col-span-9 lg:col-start-4 lg:col-span-8 relative">

            <ElementByline
            :author="page?.author_id"
            :reviewer="page?.reviewer_id"
            :published-date="page?.date"
            :updated-date="page?.date_updated"
            :show-published-date="false"
            />

            <template v-if="page?.dynamic">
              <template v-for="(section, index) in page?.dynamic">
                <ElementComparisonTable v-if="section?.item?.component == 'ComparisonTable'" />
                <ElementInlineFaq v-else-if="section.collection === 'faq_block'" :content="section.item" />
                <component v-else :is="getSectionComponent(section.collection)"
                :sectionIndex="index"
                :content="section.item"
                :sectionTotal="page.dynamic.length"
                />
              </template>
            </template>

            <ElementLegalDisclaimer v-if="!page?.suppress_legal_disclaimer" />

          </div>
        </div>
      </Container>

    </article>

    <BlockArticlesRelated
    v-if="pageCat"
    :category="pageCat" 
    :exclude="excludeSlug" 
    />
    
  </main>
</template>

<script setup>
// Remount on every navigation (its own page key, since app.vue no longer sets a global
// one). This page emits JSON-LD via useSchemaOrg, and nuxt-schema-org's schema graph is
// not cleanly replaced on client-side navigation between schema pages (stale nodes from
// the previous page linger). Remounting re-renders the schema from scratch, keeping the
// structured data correct. See content-drafts/specs/remove-global-page-key.md.
definePageMeta({ key: route => route.fullPath })

// Publish gate: a published-but-unbuilt article must 404 until the next deploy
// prerenders it, rather than being rendered live from Directus on demand.
useRequirePrerendered()

const { getItems } = useCms()
const baseFields = useFields()
const authorFields = [
  'author_id.id',
  'author_id.title',
  'author_id.slug',
  'author_id.position',
  'author_id.credentials',
  'author_id.schema_type',
  'author_id.linkedinUrl',
  'author_id.bio',
  'reviewer_id.id',
  'reviewer_id.title',
  'reviewer_id.slug',
  'reviewer_id.position',
  'reviewer_id.credentials',
  'reviewer_id.schema_type',
  'reviewer_id.linkedinUrl',
  'reviewer_id.bio'
]
const fields = [...baseFields, ...authorFields]
const route = useRoute()
const contentSections = useContentSections()

const excludeSlug = computed(() => {
  return route.params.slug
})

const currentLocale = computed(() => {
  return route.path.startsWith('/nz') ? 'nz' : 'au'
})

const { data } = await useAsyncData(
  currentLocale.value+'-article-'+route.params.slug,
  () => getItems({
    collection: 'article',
    params: {
      filter: {
        locale: { _contains: currentLocale.value },
        slug: {
          "_eq": route.params.slug
        },
        status: {
          _in: useRuntimeConfig().public.postStatus
        },
      },
      fields
    }
  })
)

// NZ→AU fallback for AU-only articles. On a full HTTP request the edge middleware
// (nzFallbackRedirect in middleware.ts) 302-redirects /nz/<slug> to the AU URL before
// this page renders. But the edge does not run on client-side (SPA) navigation, so an
// in-app link to /nz/articles/<au-only-slug> reaches this page with an empty NZ result.
// Cover that here using the already-loaded manifest (no Directus call): if the slug has
// an AU version, redirect to it; otherwise it is a genuine 404.
if (!data.value?.length) {
  const nzSlugs = useState('nzSlugs', () => null)
  const auAvailable = nzSlugs.value?.article?.au?.includes(route.params.slug)
  if (currentLocale.value === 'nz' && auAvailable) {
    await navigateTo(`/articles/${route.params.slug}`, { redirectCode: 302, external: false })
  } else {
    throw createError({
      statusCode: 404,
      message: 'Article not found',
      fatal: true
    })
  }
}

const page = data.value[0]

const pageCat = computed(() => {
  if (page?.categories?.length) {
    return page?.categories[0].category_id.slug
  }
  return null
})

const seo = computed(() => {
  if (page?.seo) {
    return useSeo(page.seo, route.fullPath)
  }
  return {}
})

useHead(seo)

// Article engagement tracking (scroll depth, active read time, in-body link +
// demo-CTA clicks) so content can be judged on engagement + pipeline influence,
// not just pageviews. Client-only, best-effort; teardown on unmount.
if (process.client) {
  let teardownEngagement = null
  onMounted(() => {
    teardownEngagement = useArticleEngagement(route.params.slug)
  })
  onBeforeUnmount(() => teardownEngagement?.())
}

const articleImage = page?.seo?.meta_image?.id
  ? useCdn(page.seo.meta_image.id, page.seo.meta_image.filename_download)
  : null

const elkerOrganization = {
  '@type': 'Organization',
  name: 'Elker',
  url: 'https://elker.com',
  logo: 'https://cdn.elker.com/elker_logo_651cfb3fa6.png'
}

const buildAuthorNode = (person, fallback) => {
  if (!person) return fallback
  const node = {
    '@type': person.schema_type === 'organization' ? 'Organization' : 'Person',
    name: person.title,
    url: 'https://elker.com/about'
  }
  if (person.linkedinUrl) {
    node.sameAs = [person.linkedinUrl]
  }
  if (person.schema_type !== 'organization') {
    if (person.position) node.jobTitle = person.position
    node.worksFor = elkerOrganization
    if (person.credentials) {
      node.hasCredential = person.credentials
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .map(name => ({
          '@type': 'EducationalOccupationalCredential',
          credentialCategory: 'degree',
          name
        }))
    }
  }
  return node
}

const articleSchema = {
  '@type': 'BlogPosting',
  headline: page?.seo?.meta_title || page?.title,
  description: page?.seo?.meta_description,
  image: articleImage,
  datePublished: page?.date,
  dateModified: page?.date_updated || page?.date,
  author: buildAuthorNode(page?.author_id, elkerOrganization),
  publisher: elkerOrganization,
  inLanguage: currentLocale.value === 'nz' ? 'en-NZ' : 'en-AU'
}

const schemaBlocks = [defineArticle(articleSchema)]
if (page?.reviewer_id) {
  schemaBlocks.push(defineWebPage({
    reviewedBy: buildAuthorNode(page.reviewer_id, null)
  }))
}
useSchemaOrg(schemaBlocks)

const getSectionComponent = key => {
  return contentSections[key]
}
</script>